import type { I18nHeadMetaInfo } from '@nuxtjs/i18n';
import type { Ref } from 'vue';

import { SeoMetaTemplate } from '~/apiClient';
import { FIRST_PAGE } from '~/constants/pagination';
import { defaultLocale, Locale } from '~i18n/i18nConfig';

type UseSlpSeoMetaProps = {
    totalItems: Ref<number | undefined>;
};

type UseSlpSeoMetaReturn = {
    suspense: () => Promise<unknown>;
};

export const useSlpSeoMeta = ({ totalItems }: UseSlpSeoMetaProps): UseSlpSeoMetaReturn => {
    // COMPOSABLES
    const { tt } = useTypedI18n();
    const route = useRoute();
    const sellingMethod = useSlpSellingMethod();
    const parsedRouteParams = useSlpParsedRouteParamsState(route);
    const { brandName, brandUrlKeys, modelName, page } = useSlpParams(sellingMethod);
    const { frontendBaseUrl } = useRuntimeConfig().public;
    const { getSlpLocalizedPath } = useSlpRouteUtils();
    const i18nHead = useLocaleHead({
        seo:
            page.value === FIRST_PAGE // Preserve page parameter in canonical only if the page is != 1
                ? true
                : {
                      canonicalQueries: ['page'],
                  },
    });

    // DATA
    // One template per filtering depth, deepest match wins. A class combined with a brand falls back to the brand
    // template: those urls are neither linked nor advertised in the sitemap, so they don't need copy of their own.
    const template = computed<SeoMetaTemplate>(() => {
        if (modelName.value) {
            return SeoMetaTemplate.ListingBrandModel;
        }

        if (brandName.value) {
            return SeoMetaTemplate.ListingBrand;
        }

        return parsedRouteParams.value.platformClass ? SeoMetaTemplate.ListingClass : SeoMetaTemplate.ListingBase;
    });
    // class holds the whole `bus + class` noun phrase, already inflected for the locale: languages that compound
    // the two words (de, nl) can't take a bare adjective, so the composition lives in the translation, not here.
    const values = computed(() => ({
        brand: brandName.value,
        model: modelName.value,
        class: parsedRouteParams.value.platformClass ? tt(`seo.class.${parsedRouteParams.value.platformClass}`) : '',
    }));
    const { title, description, suspense } = useSeoMetaTexts({ template, sellingMethod, values });

    // A multi-brand listing has no canonical of its own: `canonicalQueries` keeps `page` only, so the canonical would point at the single brand
    // sitting in the path, i.e. at a different result set. Only single-brand path urls are advertised in the sitemap, so keep combinations out.
    const robots = computed<string | undefined>(() => {
        if (!totalItems.value) {
            return 'noindex, nofollow';
        }

        return brandUrlKeys.value.length > 1 ? 'noindex, follow' : undefined;
    });

    // SEO
    const excludeAlternateLinks = (l: I18nHeadMetaInfo['link'][number]): boolean => l.rel !== 'alternate';

    const alternateLinks: I18nHeadMetaInfo['link'] = Object.values(Locale).map(locale => {
        const id = `i18n-alt-${locale}`;
        const path = getSlpLocalizedPath(locale);
        const href = `${frontendBaseUrl}${path}`;

        return { id, rel: 'alternate', href, hreflang: locale };
    });

    // Ids match the ones the i18n module assigns, so these links override the unlocalized set app.vue emits instead of duplicating it
    const xDefaultLink: I18nHeadMetaInfo['link'][number] = {
        id: 'i18n-xd',
        rel: 'alternate',
        href: `${frontendBaseUrl}${getSlpLocalizedPath(defaultLocale)}`,
        hreflang: 'x-default',
    };

    useSeoMeta({
        title: () => title.value,
        ogTitle: () => withSiteName(title.value),
        description: () => description.value,
        ogDescription: () => description.value,
        robots,
    });

    // og:url is left as the i18n module builds it, i.e. the canonical: the applied filters must not fragment the
    // social identity of the listing across one url per filter combination.
    useHead({
        link: [...i18nHead.value.link.filter(excludeAlternateLinks), xDefaultLink, ...alternateLinks],
        meta: i18nHead.value.meta,
    });

    return { suspense };
};
